import React from 'react';
import { MatDefs } from './defs';
import { INK, ModuleFront, UpperFront, ProjFront, topPrims } from './parts';
import { boxFor, WALL_LABELS } from './layout';
import { matColor, TYPES } from './catalog';

const NEON = '#4dffb8';
const FONT = 'Montserrat, Helvetica, Arial, sans-serif';
const SERIF = '"Playfair Display", Georgia, serif';
const HAND = 'Caveat, "Segoe Print", "Comic Sans MS", cursive';
const COND = 'Oswald, "Arial Narrow", Impact, sans-serif';
const fmt = (mm) => `${Math.round(mm / 10)}`;
const pts = (arr) => arr.map((q) => q.join(',')).join(' ');

export function DimH({ x1, x2, y, label, from }) {
  const narrow = x2 - x1 < 260;
  return (
    <g stroke={INK} strokeWidth={2.5}>
      <line x1={x1 - 40} y1={y} x2={x2 + 40} y2={y} />
      <line x1={x1 - 22} y1={y + 22} x2={x1 + 22} y2={y - 22} strokeWidth={5} />
      <line x1={x2 - 22} y1={y + 22} x2={x2 + 22} y2={y - 22} strokeWidth={5} />
      {from !== undefined && <><line x1={x1} y1={from} x2={x1} y2={y + 40} strokeDasharray="12 10" /><line x1={x2} y1={from} x2={x2} y2={y + 40} strokeDasharray="12 10" /></>}
      <text x={(x1 + x2) / 2} y={y - 22} textAnchor="middle" fontSize={narrow ? 50 : 66} fill={INK} stroke="none" fontFamily={FONT}>{label}</text>
    </g>
  );
}

export function DimV({ y1, y2, x, label }) {
  return (
    <g stroke={INK} strokeWidth={2.5}>
      <line x1={x} y1={y1 - 40} x2={x} y2={y2 + 40} />
      <line x1={x - 22} y1={y1 + 22} x2={x + 22} y2={y1 - 22} strokeWidth={5} />
      <line x1={x - 22} y1={y2 + 22} x2={x + 22} y2={y2 - 22} strokeWidth={5} />
      <text x={x - 26} y={(y1 + y2) / 2} textAnchor="middle" fontSize={62} fill={INK} stroke="none" fontFamily={FONT} transform={`rotate(-90 ${x - 26} ${(y1 + y2) / 2})`}>{label}</text>
    </g>
  );
}

const Tag = ({ x, y, n, sel }) => (
  <g pointerEvents="none">
    <circle cx={x} cy={y} r={74} fill={sel ? NEON : '#f7f3ec'} stroke={INK} strokeWidth={4} />
    <text x={x} y={y + 24} textAnchor="middle" fontSize={68} fontFamily={SERIF} fill={INK}>{n}</text>
  </g>
);

const Title = ({ x, y, title, sub, illus, w = 2600 }) => (illus ? (
  <g fill={INK}>
    <text x={x} y={y} fontSize={150} fontFamily={COND} fontWeight="700" letterSpacing="2">{title.toUpperCase()}</text>
    <line x1={x} y1={y + 30} x2={x + w} y2={y + 30} stroke={INK} strokeWidth={8} />
    <text x={x} y={y + 120} fontSize={58} fontFamily={COND} letterSpacing="6" fill="#6b5232">{sub}</text>
  </g>
) : (
  <g fontFamily={FONT} fill={INK}>
    <text x={x} y={y} fontSize={130} fontFamily={SERIF} fontStyle="italic">{title}</text>
    <text x={x} y={y + 95} fontSize={52} letterSpacing="14" fill="#8a6f4e">{sub}</text>
  </g>
));

function Callout({ x, y, tx, ty, text }) {
  const mx = (x + tx) / 2 + (tx > x ? -120 : 120);
  return (
    <g fill="none" stroke={INK} strokeWidth={4} strokeLinecap="round">
      <path d={`M${x} ${y + 20} Q${mx} ${(y + ty) / 2} ${tx} ${ty}`} />
      <path d={`M${tx - 22} ${ty - 34} L${tx} ${ty} L${tx + 24} ${ty - 30}`} />
      <text x={x} y={y} textAnchor="middle" fontSize={112} fontFamily={HAND} fill={INK} stroke="none">{text}</text>
    </g>
  );
}

export function Sheet({ p, vb, ctx, svgRef, children, overlay, bind, className }) {
  return (
    <svg ref={svgRef} xmlns="http://www.w3.org/2000/svg" viewBox={vb.join(' ')} preserveAspectRatio="xMidYMid meet"
      className={className} style={{ touchAction: 'none', userSelect: 'none', display: 'block', width: '100%', height: '100%' }} {...bind}>
      <MatDefs p={p} mats={ctx.mats} />
      <rect x={vb[0] - 5000} y={vb[1] - 5000} width={vb[2] + 10000} height={vb[3] + 10000} fill={ctx.illus ? '#fbf9f5' : '#f6f1e8'} />
      {ctx.illus && <rect x={vb[0] - 5000} y={vb[1] - 5000} width={vb[2] + 10000} height={vb[3] + 10000} filter={`url(#${p}-paper)`} opacity="0.6" />}
      <g>{children}</g>
      {overlay}
    </svg>
  );
}

/* ---------------- PLAN ---------------- */
export const planVB = (L) => [-950, -1080, L.W + 1900, L.D + 1980];

function PlanModule({ it, c, sel, onPick }) {
  const { spec, w, d } = it;
  const x = it.wall === 'A' ? it.x : it.u0;
  const ill = c.illus;
  const fill = ill ? (spec.zone === 'base' || spec.top ? '#e6d6bd' : '#d9b98f') : spec.zone === 'base' || spec.top ? c.F(c.mats.top) : c.F(c.front);
  const lineC = ill ? '#6b4a2b' : INK;
  const prims = topPrims({ ...it, x }, c);
  const hasDoor = spec.parts?.some((q) => q.k === 'door' || q.k === 'fridge');
  const dn = w > 650 ? 2 : 1, dw = w / dn;
  const hang = spec.parts?.some((q) => q.k === 'hang' || q.k === 'hangLong');
  const zig = Array.from({ length: Math.floor((w - 80) / 60) }).map((_, i) => `${i ? 'L' : 'M'}${x + 40 + i * 60} ${i % 2 ? d * 0.2 : d * 0.8}`).join(' ');
  return (
    <g onClick={() => onPick(it.uid)} style={{ cursor: 'pointer' }}>
      {spec.free === 'shower' ? (
        <g>
          <rect x={x} y={0} width={w} height={d} fill={c.F(c.mats.top)} stroke={INK} strokeWidth={c.sw} />
          <path d={`M${x} 0L${x + w / 2} ${d / 2}L${x + w} 0M${x} ${d}L${x + w / 2} ${d / 2}L${x + w} ${d}`} stroke={INK} strokeWidth={2} opacity="0.5" />
          <rect x={x + w / 2 - 60} y={d / 2 - 15} width={120} height={30} fill="#777" />
          <line x1={x} y1={d} x2={x + w * 0.55} y2={d} stroke="#5f8f96" strokeWidth={18} opacity="0.7" />
        </g>
      ) : spec.free === 'wc' ? (
        <g>
          <rect x={x + w / 2 - 230} y={0} width={460} height={130} fill={c.F(c.front)} stroke={INK} strokeWidth={c.sw} />
          <ellipse cx={x + w / 2} cy={330} rx={190} ry={220} fill="#f4f3f0" stroke={INK} strokeWidth={c.sw} />
        </g>
      ) : spec.free === 'desk' ? (
        <g>
          <rect x={x} y={0} width={w} height={d} fill={c.F(c.mats.top)} stroke={INK} strokeWidth={c.sw} />
          <circle cx={x + w * 0.4} cy={d + 330} r={260} fill="#d8d0c3" stroke={INK} strokeWidth={c.sw} />
        </g>
      ) : spec.free === 'planter' ? (
        <g>
          <rect x={x} y={0} width={w} height={d} fill={c.F(c.mats.top)} stroke={INK} strokeWidth={c.sw} />
          {Array.from({ length: Math.max(2, Math.round(w / 300)) }).map((_, i, a) => <circle key={i} cx={x + (w / a.length) * (i + 0.5)} cy={d / 2} r={Math.min(190, d * 0.42)} fill="#5d7a52" stroke={INK} strokeWidth={2.5} opacity="0.9" />)}
        </g>
      ) : (
        <g>
          <rect x={x} y={0} width={w} height={d} fill={fill} stroke={lineC} strokeWidth={c.sw} />
          {spec.free === 'bath' && <rect x={x + 90} y={90} width={w - 180} height={d - 180} rx={220} fill="#f5f3ef" stroke={INK} strokeWidth={c.sw} />}
          {spec.free === 'bench' && [0.33, 0.66].map((f) => <line key={f} x1={x} y1={d * f} x2={x + w} y2={d * f} stroke={INK} strokeWidth={2} opacity="0.5" />)}
          <line x1={x} y1={d} x2={x + w} y2={d} stroke={lineC} strokeWidth={c.sw * 2} />
        </g>
      )}
      {hang && (ill ? <path d={zig} fill="none" stroke="#3b2a1c" strokeWidth={5} strokeLinejoin="round" />
        : <g stroke={INK} strokeWidth={2.5}><line x1={x + 40} y1={d / 2} x2={x + w - 40} y2={d / 2} strokeDasharray="20 14" />{Array.from({ length: Math.floor((w - 80) / 70) }).map((_, i) => <line key={i} x1={x + 70 + i * 70} y1={d / 2 - 200} x2={x + 70 + i * 70} y2={d / 2 + 200} opacity="0.45" />)}</g>)}
      {hasDoor && spec.zone !== 'free' && Array.from({ length: dn }).map((_, i) => {
        const hx = dn === 2 && i === 1 ? x + w : x, dir = dn === 2 && i === 1 ? -1 : 1;
        return <path key={i} d={`M${hx} ${d}V${d + dw}A${dw} ${dw} 0 0 ${dir > 0 ? 0 : 1} ${hx + dir * dw} ${d}`} fill="none" stroke={ill ? '#8a6f4e' : INK} strokeWidth={ill ? 3 : 2} strokeDasharray="14 12" opacity="0.7" />;
      })}
      {prims.map((q, i) => q.t === 'rect' ? <rect key={i} x={q.x} y={q.z} width={q.w} height={q.d} rx={30} fill={q.fill} stroke={q.stroke || INK} strokeWidth={2.5} />
        : q.t === 'circle' ? <circle key={i} cx={q.cx} cy={q.cz} r={q.r} fill={q.fill} stroke={q.stroke || INK} strokeWidth={2.5} />
          : <line key={i} x1={q.x1} y1={q.z1} x2={q.x2} y2={q.z2} stroke={q.stroke} strokeWidth={4} />)}
      {it.up && <rect x={x + 8} y={0} width={w - 16} height={it.up.d} fill="none" stroke={INK} strokeWidth={3} strokeDasharray="34 18 6 18" />}
      {sel && <rect x={x - 20} y={-20} width={w + 40} height={d + 40} fill="none" stroke={NEON} strokeWidth={14} />}
    </g>
  );
}

export function PlanView({ L, c, sel, onPick, p, vb, svgRef, bind, className }) {
  const { W, D, items, itemsB, itemsC, island, cat } = L;
  const out = cat.outdoor;
  const tagPos = (it) => (it.wall === 'A' ? [it.x + it.w / 2, Math.min(it.d / 2, 380)] : it.wall === 'B' ? [Math.min(it.d / 2, 380), it.u0 + it.w / 2] : [W - Math.min(it.d / 2, 380), it.u0 + it.w / 2]);
  const shape = L.layout === 'U' ? 'EN U' : L.layout === 'L' ? 'EN L' : 'LINÉAIRE';
  const overlay = (
    <g>
      {items.map((it) => <DimH key={it.uid} x1={it.x} x2={it.x + it.w} y={-420} label={fmt(it.w)} from={-200} />)}
      <DimH x1={0} x2={W} y={-700} label={`${fmt(W)} cm`} />
      <DimV y1={0} y2={D} x={-620} label={`${fmt(D)} cm`} />
      {itemsB.map((it) => <DimV key={it.uid} y1={it.u0} y2={it.u0 + it.w} x={-330} label={fmt(it.w)} />)}
      {itemsC.map((it) => <DimV key={it.uid} y1={it.u0} y2={it.u0 + it.w} x={W + 400} label={fmt(it.w)} />)}
      {island && <DimV y1={L.maxD} y2={island.z0} x={island.x - 260} label={`passage ${fmt(island.z0 - L.maxD)}`} />}
      {L.all.map((it) => { const [tx, ty] = tagPos(it); return <Tag key={it.uid} x={tx} y={ty} n={it.idx + 1} sel={sel === it.uid} />; })}
      {L.layout !== 'I' && ['A', ...L.walls.slice(1)].map((k) => {
        const pos = { A: [W / 2, -880], B: [-820, D / 2], C: [W + 780, D / 2] }[k];
        return <text key={k} x={pos[0]} y={pos[1]} textAnchor="middle" fontSize={70} fontFamily={COND} fontWeight="700" fill="#6b5232">{`MUR ${k}`}</text>;
      })}
      <Title illus={c.illus} x={0} y={D + 560} title={c.illus ? `Plan d'aménagement ${shape.toLowerCase()} - vue de dessus` : 'Plan de dessus'} sub={`${cat.label.toUpperCase()} · COTES EN CM`} w={Math.min(W, 4200)} />
      <g transform={`translate(${W - 1000} ${D + 330})`}>
        {[0, 1, 2, 3].map((i) => <rect key={i} x={i * 250} y={0} width={250} height={40} fill={i % 2 ? '#f6f1e8' : INK} stroke={INK} strokeWidth={3} />)}
        <text x={0} y={120} fontSize={56} fontFamily={FONT} fill={INK}>0</text>
        <text x={1000} y={120} fontSize={56} fontFamily={FONT} fill={INK} textAnchor="end">1 m</text>
      </g>
    </g>
  );
  const wallFill = c.illus ? '#141414' : `url(#${p}-hatch)`;
  return (
    <Sheet p={p} vb={vb || planVB(L)} ctx={c} svgRef={svgRef} overlay={overlay} bind={bind} className={className}>
      <rect x={0} y={0} width={W} height={D} fill={c.illus ? '#f3efe8' : c.F(c.mats.floor)} opacity={c.illus ? 1 : 0.4} />
      <g fill={wallFill} stroke={INK} strokeWidth={c.sw}>
        <rect x={-200} y={-200} width={W + 400} height={200} />
        {out ? [[-150, D - 150], [W - 150, D - 150], [-150, -150], [W - 150, -150]].map(([px, pz], i) => <rect key={i} x={px} y={pz} width={300} height={300} fill={c.F(c.front)} />) : (
          <>
            <rect x={-200} y={0} width={200} height={D} />
            <rect x={W} y={0} width={200} height={D} />
            <rect x={-200} y={D} width={W - 1100} height={200} />
            <rect x={W - 400} y={D} width={600} height={200} />
          </>
        )}
      </g>
      {out && <rect x={0} y={0} width={W} height={D} fill="none" stroke={INK} strokeWidth={3} strokeDasharray="60 30" />}
      {!out && <path d={`M${W - 400} ${D}V${D - 900}A900 900 0 0 0 ${W - 1300} ${D}`} fill="none" stroke={c.illus ? '#8a6f4e' : INK} strokeWidth={4} strokeDasharray="16 12" />}
      {items.map((it) => <PlanModule key={it.uid} it={it} c={c} sel={sel === it.uid} onPick={onPick} />)}
      <g transform="matrix(0 1 1 0 0 0)">{itemsB.map((it) => <PlanModule key={it.uid} it={it} c={c} sel={sel === it.uid} onPick={onPick} />)}</g>
      <g transform={`matrix(0 1 -1 0 ${W} 0)`}>{itemsC.map((it) => <PlanModule key={it.uid} it={it} c={c} sel={sel === it.uid} onPick={onPick} />)}</g>
      {island && (
        <g onClick={() => onPick('island')} style={{ cursor: 'pointer' }}>
          <rect x={island.x} y={island.z0} width={island.w} height={island.d} fill={c.illus ? '#e6d6bd' : island.spec.zone === 'free' ? c.F(c.front) : c.F(c.mats.top)} stroke={INK} strokeWidth={c.sw * 1.5} />
          <text x={island.x + island.w / 2} y={island.z0 + island.d / 2 + 22} textAnchor="middle" fontSize={64} fontFamily={c.illus ? HAND : FONT} fill={INK}>{cat.island.label}</text>
          {sel === 'island' && <rect x={island.x - 20} y={island.z0 - 20} width={island.w + 40} height={island.d + 40} fill="none" stroke={NEON} strokeWidth={14} />}
        </g>
      )}
    </Sheet>
  );
}

/* ---------------- ELEVATION ---------------- */
export const elevVB = (L, wall = 'A') => [-850, -720, L.wall[wall].W + 1800, L.H + 1880];

export function ElevationView({ L, c, sel, onPick, p, vb, svgRef, bind, className, wall = 'A' }) {
  const { H, island, cat } = L;
  const { W, items } = L.wall[wall] || L.wall.A;
  const hasBase = items.some((i) => i.spec.zone === 'base');
  const upper = items.find((i) => i.up && i.up.elev);
  const overlay = (
    <g>
      {items.map((it) => <Tag key={it.uid} x={it.ex + it.w / 2} y={H + 200} n={it.idx + 1} sel={sel === it.uid} />)}
      {items.map((it) => <DimH key={it.uid} x1={it.ex} x2={it.ex + it.w} y={H + 430} label={fmt(it.w)} />)}
      <DimH x1={0} x2={W} y={H + 650} label={`${fmt(W)} cm`} />
      <DimV y1={0} y2={H} x={W + 560} label={`${fmt(H)} cm`} />
      {hasBase && <DimV y1={H - 900} y2={H} x={W + 300} label="90" />}
      {upper && <DimV y1={H - upper.up.elev - upper.up.h} y2={H - upper.up.elev} x={W + 300} label={fmt(upper.up.h)} />}
      {c.illus && items.map((it, i) => (
        <Callout key={it.uid} x={it.ex + it.w / 2 + (i % 2 ? 60 : -60)} y={i % 2 ? -470 : -300} tx={it.ex + it.w / 2} ty={H - it.elev - it.h + Math.min(260, it.h * 0.3)} text={TYPES[it.type].label} />
      ))}
      <Title illus={c.illus} x={0} y={H + 960} title={c.illus ? 'Agencements intérieurs en élévation' : 'Élévation'} sub={`${L.layout === 'I' ? 'MUR PRINCIPAL' : WALL_LABELS[wall].toUpperCase()} · ${cat.label.toUpperCase()}`} w={Math.min(W, 4400)} />
    </g>
  );
  return (
    <Sheet p={p} vb={vb || elevVB(L, wall)} ctx={c} svgRef={svgRef} overlay={overlay} bind={bind} className={className}>
      <rect x={0} y={0} width={W} height={H} fill={cat.outdoor ? `url(#${p}-sky)` : c.F(c.mats.wall)} />
      {cat.outdoor && <rect x={0} y={H * 0.12} width={W} height={H * 0.88} fill={c.F(c.mats.wall)} />}
      {c.led && !cat.outdoor && <rect x={0} y={0} width={W} height={260} fill={`url(#${p}-led)`} opacity="0.45" />}
      <g fill={c.illus ? '#1b1b1b' : `url(#${p}-hatch)`} stroke={INK} strokeWidth={c.sw}>
        <rect x={-200} y={-200} width={200} height={H + 320} />
        <rect x={W} y={-200} width={200} height={H + 320} />
        <rect x={-200} y={-200} width={W + 400} height={200} />
        <rect x={-200} y={H} width={W + 400} height={120} />
      </g>
      <rect x={0} y={H - 12} width={W} height={12} fill={c.F(c.mats.floor)} />
      {items.map((it) => (
        <g key={it.uid} onClick={() => onPick(it.uid)} style={{ cursor: 'pointer' }}>
          <g transform={`translate(${it.ex} ${H - it.elev - it.h})`}>
            <ModuleFront it={it} c={c} />
            {sel === it.uid && <rect x={-14} y={-14} width={it.w + 28} height={it.h + 28} fill="none" stroke={NEON} strokeWidth={14} />}
          </g>
          {it.up && <g transform={`translate(${it.ex} ${H - it.up.elev - it.up.h})`}><UpperFront up={it.up} w={it.w} c={{ ...c, ceil: H - it.up.elev - it.up.h }} seed={it.idx * 5 + 1} /></g>}
        </g>
      ))}
      {island && wall === 'A' && (
        <g onClick={() => onPick('island')} style={{ cursor: 'pointer' }}>
          <rect x={island.x} y={H - island.h} width={island.w} height={island.h} fill="#fff" fillOpacity="0.18" stroke={sel === 'island' ? NEON : INK} strokeWidth={sel === 'island' ? 12 : 5} strokeDasharray="40 24" />
          <text x={island.x + island.w / 2} y={H - island.h - 40} textAnchor="middle" fontSize={56} fontFamily={FONT} fill={INK}>{cat.island.label} (avant-plan)</text>
        </g>
      )}
    </Sheet>
  );
}

/* ---------------- PERSPECTIVE ---------------- */
export function camera(L, view = 0) {
  const { W, H, D } = L;
  const E = W * 1.05 + 2400 + (L.layout !== 'I' ? 800 : 0);
  const camX = W / 2 + view * W * 0.38, camY = 1600;
  const s = (z) => E / (E - z);
  const P = (x, y, z) => { const k = s(z); return [camX + (x - camX) * k, H - camY - (y - camY) * k]; };
  const zn = Math.min(D, E * 0.62);
  return { E, camX, camY, s, P, zn, H };
}

export const perspVB = (L, view = 0) => {
  const cam = camera(L, view);
  const zv = cam.E * (1 - 1 / 1.42);
  const a = cam.P(0, 0, zv), b = cam.P(L.W, L.H, zv);
  return [a[0], b[1] - 120, b[0] - a[0], a[1] - b[1] + 260];
};

function BoxFaces({ x, w, y0, y1, d, z0 = 0, cam, fillSide, fillTop, sw, illus }) {
  const { P, camX, camY } = cam;
  const faces = [];
  if (x + w <= camX) faces.push([P(x + w, y0, z0), P(x + w, y1, z0), P(x + w, y1, z0 + d), P(x + w, y0, z0 + d)]);
  if (x >= camX) faces.push([P(x, y0, z0), P(x, y1, z0), P(x, y1, z0 + d), P(x, y0, z0 + d)]);
  const top = y1 < camY ? [P(x, y1, z0), P(x + w, y1, z0), P(x + w, y1, z0 + d), P(x, y1, z0 + d)] : null;
  const bot = y0 > camY ? [P(x, y0, z0), P(x + w, y0, z0), P(x + w, y0, z0 + d), P(x, y0, z0 + d)] : null;
  return (
    <g stroke={INK} strokeWidth={sw} strokeLinejoin="round">
      {faces.map((f, i) => <g key={i}><polygon points={pts(f)} fill={fillSide} /><polygon points={pts(f)} fill="#000" opacity={illus ? 0.34 : 0.22} /></g>)}
      {top && <g><polygon points={pts(top)} fill={fillTop} /><polygon points={pts(top)} fill="#fff" opacity="0.12" /></g>}
      {bot && <g><polygon points={pts(bot)} fill={fillSide} /><polygon points={pts(bot)} fill="#000" opacity="0.35" /></g>}
    </g>
  );
}

function TopPrims3D({ it, y, cam, c }) {
  return topPrims(it, c).map((q, i) => {
    if (q.t === 'rect') return <polygon key={i} points={pts([cam.P(q.x, y, q.z), cam.P(q.x + q.w, y, q.z), cam.P(q.x + q.w, y, q.z + q.d), cam.P(q.x, y, q.z + q.d)])} fill={q.fill} stroke={INK} strokeWidth={2} />;
    if (q.t === 'circle') return <polygon key={i} points={pts(Array.from({ length: 20 }).map((_, k) => cam.P(q.cx + q.r * Math.cos((k / 20) * 6.283), y, q.cz + q.r * Math.sin((k / 20) * 6.283))))} fill={q.fill} stroke={q.stroke || INK} strokeWidth={2} />;
    return null;
  });
}

function Persp3DModule({ it, c, cam, sel, onPick, isIsland }) {
  const z0 = isIsland ? it.z0 : 0;
  const zf = z0 + (it.spec.free === 'wc' ? it.d * 0.5 : it.spec.free === 'shower' ? 0 : it.d);
  const box = boxFor(it);
  const topFill = it.spec.zone === 'base' || it.spec.top || ['bath', 'planter', 'desk'].includes(it.spec.free) ? c.F(c.mats.top) : c.F(c.front);
  const [fx, fy] = cam.P(it.x, it.elev + it.h, zf);
  const k = cam.s(zf);
  return (
    <g onClick={() => onPick(it.uid)} style={{ cursor: 'pointer' }}>
      {it.spec.free === 'shower' && <polygon points={pts([cam.P(it.x, 4, 0), cam.P(it.x + it.w, 4, 0), cam.P(it.x + it.w, 4, it.d), cam.P(it.x, 4, it.d)])} fill={c.F(c.mats.top)} stroke={INK} strokeWidth={c.sw} />}
      {box && <BoxFaces x={it.x} w={it.w} y0={box.y0} y1={box.y1} d={it.d} z0={z0} cam={cam} fillSide={c.F(c.front)} fillTop={topFill} sw={c.sw} illus={c.illus} />}
      {box && <TopPrims3D it={it} y={box.y1 + 2} cam={{ ...cam, P: (x, y, z) => cam.P(x, y, z + z0) }} c={c} />}
      <g transform={`translate(${fx} ${fy}) scale(${k})`}>
        <ModuleFront it={it} c={it.spec.free === 'shower' ? { ...c, persp: true } : c} />
        {sel && <rect x={-14} y={-14} width={it.w + 28} height={it.h + 28} fill="none" stroke={NEON} strokeWidth={14} />}
      </g>
      {it.spec.free === 'shower' && <polygon points={pts([cam.P(it.x, 0, it.d), cam.P(it.x + it.w * 0.55, 0, it.d), cam.P(it.x + it.w * 0.55, 2000, it.d), cam.P(it.x, 2000, it.d)])} fill={`url(#${c.p}-glass)`} stroke="#1a1a1a" strokeWidth={10} />}
      {it.up && (() => {
        const u = it.up; const [ux, uy] = cam.P(it.x, u.elev + u.h, u.d); const uk = cam.s(u.d);
        return (
          <g>
            {u.parts[0].k !== 'hood' && <BoxFaces x={it.x} w={it.w} y0={u.elev} y1={u.elev + u.h} d={u.d} cam={cam} fillSide={c.F(c.front)} fillTop={c.F(c.front)} sw={c.sw} illus={c.illus} />}
            <g transform={`translate(${ux} ${uy}) scale(${uk})`}><UpperFront up={u} w={it.w} c={{ ...c, ceil: cam.H - u.elev - u.h }} seed={it.idx * 5 + 1} /></g>
          </g>
        );
      })()}
    </g>
  );
}

function SideModule({ it, c, cam, sel, onPick, W }) {
  const left = it.wall === 'B';
  const { P, camY } = cam;
  const fxp = left ? it.d : W - it.d, wx = left ? 0 : W;
  const zA = it.u0, zB = it.u0 + it.w;
  const zOf = (lx) => (left ? zB - lx : zA + lx);
  const faces = (y0, y1, d, h, parts, zone, top) => {
    const fx = left ? d : W - d;
    const map = (lx, ly) => P(fx, y1 - ly, zOf(lx));
    const end = [P(wx, y0, zB), P(fx, y0, zB), P(fx, y1, zB), P(wx, y1, zB)];
    const topF = y1 < camY ? [P(wx, y1, zA), P(fx, y1, zA), P(fx, y1, zB), P(wx, y1, zB)] : null;
    const bot = y0 > camY ? [P(wx, y0, zA), P(fx, y0, zA), P(fx, y0, zB), P(wx, y0, zB)] : null;
    return (
      <g>
        {topF && <polygon points={pts(topF)} fill={top ? c.F(c.mats.top) : c.F(c.front)} stroke={INK} strokeWidth={c.sw} />}
        {bot && <g><polygon points={pts(bot)} fill={c.F(c.front)} stroke={INK} strokeWidth={c.sw} /><polygon points={pts(bot)} fill="#000" opacity="0.35" /></g>}
        <ProjFront it={it} c={c} map={map} h={h} parts={parts} zone={zone} top={top} />
        <polygon points={pts(end)} fill={c.F(c.front)} stroke={INK} strokeWidth={c.sw} /><polygon points={pts(end)} fill="#000" opacity="0.2" />
      </g>
    );
  };
  const { spec } = it;
  const top = spec.zone === 'base' ? 40 : spec.zone === 'float' && spec.top ? 30 : 0;
  const sp = P(fxp, it.elev + it.h, zA), sq = P(fxp, it.elev, zB);
  return (
    <g onClick={() => onPick(it.uid)} style={{ cursor: 'pointer' }}>
      {faces(it.elev, it.elev + it.h, it.d, it.h, spec.parts, spec.zone, top)}
      {it.up && it.up.parts[0].k !== 'hood' && faces(it.up.elev, it.up.elev + it.up.h, it.up.d, it.up.h, it.up.parts, 'upper', 0)}
      {sel && <polygon points={pts([sp, P(fxp, it.elev + it.h, zB), sq, P(fxp, it.elev, zA)])} fill="none" stroke={NEON} strokeWidth={14} />}
    </g>
  );
}

export function PerspectiveView({ L, c, sel, onPick, p, vb, svgRef, bind, className, view = 0 }) {
  const { W, H, items, island, cat } = L;
  const cam = camera(L, view);
  const { P, zn } = cam;
  const floor = [P(0, 0, 0), P(W, 0, 0), P(W, 0, zn), P(0, 0, zn)];
  const ceil = [P(0, H, 0), P(W, H, 0), P(W, H, zn), P(0, H, zn)];
  const lw = [P(0, 0, 0), P(0, H, 0), P(0, H, zn), P(0, 0, zn)];
  const rw = [P(W, 0, 0), P(W, H, 0), P(W, H, zn), P(W, 0, zn)];
  const fm = c.mats.floor;
  const stoneFloor = fm.startsWith('sol_');
  const darkFloor = ['parquet_noir', 'deck_teck', 'parquet_fume', 'sol_beton'].includes(fm);
  const sorted = [...items].sort((a, b) => Math.abs(b.x + b.w / 2 - cam.camX) - Math.abs(a.x + a.w / 2 - cam.camX));
  const near = [...L.itemsB, ...L.itemsC].map((it) => ({ it, z: it.u0 + it.w }));
  if (island) near.push({ it: island, z: island.z0 + island.d, isl: true });
  near.sort((a, b) => a.z - b.z);
  const vbx = vb || perspVB(L, view);
  const overlay = (
    <g>
      <rect x={vbx[0]} y={vbx[1] + vbx[3] - 420} width={2900} height={420} fill="#fbf9f5" opacity="0.92" />
      <Title illus={c.illus} x={vbx[0] + 120} y={vbx[1] + vbx[3] - 240} title={c.illus ? "Perspective d'ambiance" : 'Perspective'} sub={`${cat.label.toUpperCase()} · ${c.illus ? 'ILLUSTRATION COLORÉE' : 'TRAIT TECHNIQUE'}`} w={2500} />
    </g>
  );
  return (
    <Sheet p={p} vb={vbx} ctx={c} svgRef={svgRef} overlay={overlay} bind={bind} className={className}>
      <g stroke={INK} strokeWidth={c.sw}>
        {cat.outdoor ? <polygon points={pts(ceil)} fill={`url(#${p}-sky)`} stroke="none" /> : <polygon points={pts(ceil)} fill="#f3efe8" />}
        <polygon points={pts(lw)} fill={c.F(c.mats.wall)} /><polygon points={pts(lw)} fill="#000" opacity="0.07" stroke="none" />
        <polygon points={pts(rw)} fill={c.F(c.mats.wall)} /><polygon points={pts(rw)} fill="#000" opacity="0.13" stroke="none" />
        <rect x={0} y={0} width={W} height={H} fill={c.F(c.mats.wall)} />
        <polygon points={pts(floor)} fill={matColor(fm)} />
        <polygon points={pts(floor)} fill={c.illus ? `url(#${p}-floorShade)` : '#000'} opacity={c.illus ? 1 : 0.06} stroke="none" />
      </g>
      <g stroke={darkFloor ? '#d9cbb5' : INK} strokeWidth={2} opacity={darkFloor ? 0.28 : 0.35}>
        {stoneFloor
          ? [...Array.from({ length: Math.ceil(W / 600) }).map((_, i) => [P(i * 600, 0, 0), P(i * 600, 0, zn)]), ...Array.from({ length: Math.ceil(zn / 600) }).map((_, i) => [P(0, 0, i * 600), P(W, 0, i * 600)])].map(([a, b], i) => <line key={i} x1={a[0]} y1={a[1]} x2={b[0]} y2={b[1]} />)
          : Array.from({ length: Math.ceil(W / 190) }).map((_, i) => { const a = P(i * 190, 0, 0), b = P(i * 190, 0, zn); return <line key={i} x1={a[0]} y1={a[1]} x2={b[0]} y2={b[1]} />; })}
      </g>
      {cat.outdoor ? (
        <g stroke={INK} strokeWidth={c.sw}>
          {Array.from({ length: Math.ceil(W / 520) + 1 }).map((_, i) => <polygon key={i} points={pts([P(i * 520 - 40, H - 120, 0), P(i * 520 + 40, H - 120, 0), P(i * 520 + 40, H - 120, zn), P(i * 520 - 40, H - 120, zn)])} fill={c.F('teck')} />)}
        </g>
      ) : (
        <g>
          {c.led && <line x1={P(0, H - 25, 0)[0]} y1={P(0, H - 25, 0)[1]} x2={P(W, H - 25, 0)[0]} y2={P(W, H - 25, 0)[1]} stroke="#ffd59a" strokeWidth={c.illus ? 70 : 40} filter={`url(#${p}-glow)`} />}
          {[0.25, 0.5, 0.75].flatMap((fx) => [900, 2200].map((z) => {
            const q = P(W * fx, H, z); const r = 46 * cam.s(z);
            return <ellipse key={`${fx}-${z}`} cx={q[0]} cy={q[1]} rx={r} ry={r * 0.32} fill="#fff6dc" stroke={INK} strokeWidth={2} />;
          }))}
        </g>
      )}
      {sorted.map((it) => <Persp3DModule key={it.uid} it={it} c={c} cam={cam} sel={sel === it.uid} onPick={onPick} />)}
      {near.map(({ it, isl }) => (isl
        ? <Persp3DModule key="island" it={it} c={c} cam={cam} sel={sel === 'island'} onPick={onPick} isIsland />
        : <SideModule key={it.uid} it={it} c={c} cam={cam} W={W} sel={sel === it.uid} onPick={onPick} />))}
    </Sheet>
  );
}
